import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import HlsVideo from "@/components/HlsVideo";
import { resolveMedia } from "@/lib/media";
import { ArrowLeft, Heart, ChatCircle, ShareFat } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Watch — dedicated long-form player page at /w/:postId.
 *
 * - Loads the post detail
 * - Fetches saved watch progress → resumes from that position
 * - Shows creator/title/description + engagement actions
 * - Video renders with full ABR + quality selector via HlsVideo (Video.js)
 */
export default function Watch() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [resumeFrom, setResumeFrom] = useState(0);
  const [loading, setLoading] = useState(true);
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: postData }, { data: progress }] = await Promise.all([
          api.get(`/posts/${postId}/detail`),
          api.get(`/watch/progress/${postId}`).catch(() => ({ data: { position_seconds: 0 } })),
        ]);
        if (cancelled) return;
        setPost(postData);
        setLikeCount(postData.like_count || 0);
        setLiked(!!postData.is_liked);
        setResumeFrom(progress.position_seconds || 0);
        // Record view (respects first-10-free-views rule server-side)
        try {
          const { data: viewRes } = await api.post(`/posts/${postId}/view`);
          if (viewRes?.free_view && viewRes?.free_views_remaining !== undefined) {
            toast(`Free preview · ${viewRes.free_views_remaining} free views left`, { duration: 3000 });
          }
        } catch { /* silent */ }
      } catch (e) {
        toast.error("Video not found");
        navigate("/");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId, navigate]);

  const src = useMemo(() => {
    if (!post) return null;
    return resolveMedia(post.hls_url || post.media_url);
  }, [post]);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${postId}/like`);
      setLiked(!!data.liked);
      setLikeCount((c) => c + (data.liked ? 1 : -1));
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits. Redirecting to Buy Credits…");
        setTimeout(() => navigate("/buy-points"), 800);
      }
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ url, title: post?.caption || "Tain" }); } catch { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      } catch { /* ignore */ }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="watch-loading">
        Loading…
      </div>
    );
  }
  if (!post || !src) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        Video unavailable.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="watch-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-black/80 backdrop-blur border-b border-white/10 px-3 py-2 flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/10"
          data-testid="watch-back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
        <span className="text-sm font-heading font-black truncate">
          {post.caption || "Untitled video"}
        </span>
      </div>

      {/* Player — responsive, source-aspect preserving */}
      <div className="w-full bg-black flex items-center justify-center">
        <div className="w-full max-w-6xl">
          <HlsVideo
            src={src}
            poster={post.thumbnail_url ? resolveMedia(post.thumbnail_url) : undefined}
            postId={postId}
            resumeFrom={resumeFrom}
            controls
            autoplay
            className="w-full"
          />
        </div>
      </div>

      {/* Metadata + actions */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <h1 className="font-heading text-xl md:text-2xl font-black leading-tight" data-testid="watch-title">
          {post.caption || "Untitled"}
        </h1>
        <div className="mt-3 flex items-center gap-3">
          {post.author?.picture && (
            <img src={post.author.picture} alt="" className="w-10 h-10 rounded-full" />
          )}
          <div>
            <p className="font-heading font-bold">{post.author?.name || "Unknown"}</p>
            <p className="text-white/50 text-xs">
              {(post.view_count || 0).toLocaleString()} views
            </p>
          </div>
          <div className="ml-auto flex gap-2">
            <button
              onClick={toggleLike}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full ${liked ? "bg-red-500/25 ring-1 ring-red-400" : "bg-white/10 hover:bg-white/20"}`}
              data-testid="watch-like-btn"
            >
              <Heart size={18} weight={liked ? "fill" : "bold"} className={liked ? "text-red-400" : ""} />
              <span className="text-sm font-bold">{likeCount}</span>
            </button>
            <button
              onClick={share}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20"
              data-testid="watch-share-btn"
            >
              <ShareFat size={18} weight="bold" />
            </button>
          </div>
        </div>
        {post.caption && post.caption.length > 60 && (
          <p className="mt-3 text-white/80 whitespace-pre-wrap text-sm" data-testid="watch-description">
            {post.caption}
          </p>
        )}
      </div>
    </div>
  );
}
